import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { ArrowLeftOutlined, FileExcelOutlined, CheckCircleFilled } from "@ant-design/icons";
import { SbTable, SbTag, SbButton, SbTabs, SbTooltip } from "uiCore";
import { PageHeaderStyled, HeaderLeftStyled, HeaderRightStyled, HeadingRowStyled } from "styles/common.styles";
import { ClickableTableWrapperStyled } from "./sharedTable.styles";
import { PageTransitionStyled } from "./talentMobility.animations";
import { BackLinkStyled } from "./RoleDetailV2.styles";
import { SummaryBucketsRowStyled, SummaryBucketCardStyled, RolePanelStyled } from "./PaicOrgRedesignV2.styles";
import ShortlistTag from "./components/ShortlistTag";
import SkillGapList from "./components/SkillGapList";
import IndexTooltip, { IndexInfoLabel } from "./components/IndexTooltip";
import { INDEX_FULL_NAMES } from "./talentMobility.constants";
import { useOrgDesignVersions } from "Pages/OrgDesign/useOrgDesignData";
import { ROLES, PEOPLE, SKILLS_CATALOG, PERSON_SKILLS, getSkillGap, computeMatchForRole } from "./mockData/talentMobility.mock";
import { ORG_DESIGN_TM_ROLE_ALIASES, AUGMENTED_SKILL_DELTA } from "./mockData/paicIntegration.mock";
import { getScoreColor } from "Utils/scoreColor.utils";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import useAvailabilityStore from "Store/useAvailabilityStore";
import usePaicExecutionStore from "Store/usePaicExecutionStore";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

const buildAugmentedGap = (tmRoleId, personId, delta) => {
  const baseGap = getSkillGap(tmRoleId, personId);
  const redundantIds = delta?.redundantSkillIds || [];
  const kept = baseGap.filter((s) => !redundantIds.includes(s.skillId));
  const redundant = baseGap.filter((s) => redundantIds.includes(s.skillId));
  const personSkills = PERSON_SKILLS[personId] || [];
  const added = (delta?.addedSkills || []).map((a) => {
    const skill = SKILLS_CATALOG.find((s) => s.id === a.skillId);
    const current = personSkills.find((ps) => ps.skillId === a.skillId)?.current ?? 0;
    return {
      skillId: a.skillId,
      skillName: skill?.name || a.skillId,
      description: skill?.description || null,
      required: a.required,
      current,
      priority: a.required >= 4,
    };
  });
  const newGap = [...kept, ...added];
  const total = newGap.reduce((sum, s) => sum + s.required, 0);
  const achieved = newGap.reduce((sum, s) => sum + Math.min(s.current, s.required), 0);
  const newSri = total > 0 ? Math.round((achieved / total) * 100) : 0;
  return { newGap, redundant, newSri };
};

// Roles Reduced tab — reuses the exact Redeployment reduction-detail pattern
// (occupants ranked by SMI, cutoff at the snapshot's approved after_hc, mark
// below-cutoff people available) for whichever roles have a linked TM role.
const ReducedRolesTab = ({ roles, design }) => {
  const thresholds = useMatchThresholds();
  const availableMap = useAvailabilityStore((state) => state.available);
  const markAvailable = useAvailabilityStore((state) => state.markAvailable);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const reducedRoles = roles.filter((r) => ["REDUCED", "REDUNDANT"].includes(design[r.sb_unique_id]?.status));

  return (
    <ClickableTableWrapperStyled>
      <SbTable
        columns={[
          { title: "Role", dataIndex: "job_title" },
          { title: "Before HC", dataIndex: "total_positions", align: "center" },
          { title: "After HC (Approved)", key: "afterHc", align: "center", render: (_, r) => design[r.sb_unique_id]?.after_hc ?? r.total_positions },
          { title: "Rationale", key: "rationale", render: (_, r) => design[r.sb_unique_id]?.rationale || "—" },
        ]}
        dataSource={reducedRoles}
        rowKey="sb_unique_id"
        pagination={false}
        onRow={(record) => ({
          onClick: () =>
            setExpandedKeys((prev) =>
              prev.includes(record.sb_unique_id) ? prev.filter((k) => k !== record.sb_unique_id) : [...prev, record.sb_unique_id]
            ),
        })}
        expandable={{
          expandedRowKeys: expandedKeys,
          showExpandColumn: false,
          rowExpandable: () => true,
          expandedRowRender: (record) => {
            const tmRoleId = ORG_DESIGN_TM_ROLE_ALIASES[record.sb_unique_id];
            const tmRole = ROLES.find((r) => r.id === tmRoleId);
            if (!tmRole) {
              return (
                <RolePanelStyled>
                  <Text type="secondary">No linked Talent Mobility roster for this role yet.</Text>
                </RolePanelStyled>
              );
            }
            const afterHc = design[record.sb_unique_id]?.after_hc ?? record.total_positions;
            const occupants = PEOPLE.filter((p) => p.currentRoleId === tmRoleId)
              .map((p) => ({ ...computeMatchForRole(tmRoleId, p.id), person: p }))
              .sort((a, b) => b.matchPct - a.matchPct);
            return (
              <RolePanelStyled>
                <div className="nested-title">People currently in linked role: {tmRole.title}</div>
                {occupants.map((o, i) => (
                  <div key={o.person.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", alignItems: "center" }}>
                    <span>{o.person.name} <ShortlistTag roleId={tmRoleId} personId={o.person.id} /></span>
                    <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <IndexTooltip index="SMI"><SbTag color={getScoreColor(o.matchPct, thresholds)}>{o.matchPct}% SMI</SbTag></IndexTooltip>
                      {i >= afterHc ? <SbTag color="error">At Risk</SbTag> : <SbTag color="success">Retained</SbTag>}
                      {i >= afterHc && (
                        availableMap[o.person.id] ? (
                          <SbTag color="success" icon={<CheckCircleFilled />}>Available</SbTag>
                        ) : (
                          <SbButton size="small" buttonType="primary" onClick={() => markAvailable(o.person.id, "paic-reduced")}>
                            Mark Available
                          </SbButton>
                        )
                      )}
                    </span>
                  </div>
                ))}
              </RolePanelStyled>
            );
          },
        }}
      />
    </ClickableTableWrapperStyled>
  );
};

// Own component (not inlined in AugmentedRolesTab's map) so buildAugmentedGap's
// result can be memoized on the (stable) tmRoleId/personId/delta inputs — inlined
// in the parent's render, it produced a brand-new `newGap` array on every render
// of AugmentedRolesTab (e.g. from an unrelated useAvailabilityStore update),
// which silently reset SkillGapList's internal pagination back to page 1.
const AugmentedOccupantPanel = ({ tmRoleId, personId, personName, delta }) => {
  const oldMatch = computeMatchForRole(tmRoleId, personId);
  const { newGap, redundant, newSri } = useMemo(
    () => buildAugmentedGap(tmRoleId, personId, delta),
    [tmRoleId, personId, delta]
  );
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <strong>{personName}</strong>
        <span style={{ display: "flex", gap: 10 }}>
          <SbTooltip content={`SRI (${INDEX_FULL_NAMES.SRI}) against the role's previous skill requirements.`}><SbTag>{oldMatch.matchPct}% Old SRI</SbTag></SbTooltip>
          <SbTooltip content={`SRI (${INDEX_FULL_NAMES.SRI}) against the role's new, augmented skill requirements.`}><SbTag color="processing">{newSri}% New SRI</SbTag></SbTooltip>
        </span>
      </div>
      <SkillGapList skillGap={newGap} showScore={false} />
      {redundant.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {redundant.map((s) => (
            <SbTag key={s.skillId} color="error" style={{ marginRight: 6 }}>{s.skillName} — no longer required</SbTag>
          ))}
        </div>
      )}
    </div>
  );
};

const AugmentedRolesTab = ({ roles, design }) => {
  const augmentedRoles = roles.filter((r) => design[r.sb_unique_id]?.status === "AI AUGMENTED");

  return (
    <div>
      {augmentedRoles.map((r) => {
        const tmRoleId = ORG_DESIGN_TM_ROLE_ALIASES[r.sb_unique_id];
        const tmRole = ROLES.find((tr) => tr.id === tmRoleId);
        const delta = AUGMENTED_SKILL_DELTA[r.sb_unique_id];
        if (!tmRole) {
          return (
            <RolePanelStyled key={r.sb_unique_id}>
              <div className="nested-title">{r.job_title}</div>
              <Text type="secondary">No linked Talent Mobility roster or skill-drift data for this role yet.</Text>
            </RolePanelStyled>
          );
        }
        const occupants = PEOPLE.filter((p) => p.currentRoleId === tmRoleId);
        return (
          <RolePanelStyled key={r.sb_unique_id}>
            <div className="nested-title">{r.job_title} (linked: {tmRole.title}) — old vs new skill requirements</div>
            {occupants.map((p) => (
              <AugmentedOccupantPanel key={p.id} tmRoleId={tmRoleId} personId={p.id} personName={p.name} delta={delta} />
            ))}
          </RolePanelStyled>
        );
      })}
      {!augmentedRoles.length && <Text type="secondary" style={{ padding: "0 24px" }}>No augmented roles in this version.</Text>}
    </div>
  );
};

const AddedRolesTab = ({ newRoles }) => {
  return (
    <div>
      {newRoles.map((nr) => (
        <RolePanelStyled key={nr.role_title}>
          <div className="nested-title">{nr.role_title} (new, {nr.after_hc} HC)</div>
          <Text type="secondary">
            No skill-inference / job description exists yet for this new role — once one is defined, org-wide <IndexInfoLabel index="SMI" /> matching runs
            automatically here to surface internal candidates.
          </Text>
        </RolePanelStyled>
      ))}
      {!newRoles.length && <Text type="secondary" style={{ padding: "0 24px" }}>No new roles in this version.</Text>}
    </div>
  );
};

const ANALYTICS_FROM = "talent_mobility_paic_org_redesign";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.PAIC_ORG_REDESIGN_VIEWED;

const PaicOrgRedesignV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const { versions } = useOrgDesignVersions();
  const results = usePaicExecutionStore((state) => state.results);
  const saveResult = usePaicExecutionStore((state) => state.saveResult);
  const [selectedVersionId, setSelectedVersionId] = useState(null);

  const selectedVersion = versions.find((v) => v.versionId === selectedVersionId) || null;
  const snapshot = selectedVersion?.snapshot;

  const buckets = useMemo(() => {
    if (!snapshot?.status_counts) return null;
    const c = snapshot.status_counts;
    return {
      decreased: (c.REDUCED || 0) + (c.REDUNDANT || 0),
      augmented: c["AI AUGMENTED"] || 0,
      increased: c.CREATED || 0,
      noChange: c.UNCHANGED || 0,
    };
  }, [snapshot]);

  const handleExportCsv = () => {
    if (!snapshot) return;
    const rows = snapshot.roles.map((r) => ({
      Role: r.job_title,
      "Before HC": r.total_positions,
      "After HC": snapshot.design[r.sb_unique_id]?.after_hc ?? r.total_positions,
      Status: snapshot.design[r.sb_unique_id]?.status || "UNCHANGED",
      Rationale: snapshot.design[r.sb_unique_id]?.rationale || "",
    }));
    exportRowsAsCsv(rows, `paic-org-redesign-${selectedVersion.versionId}`);
  };

  const handleSaveResults = () => {
    saveResult({
      versionId: selectedVersion.versionId,
      versionLabel: selectedVersion.label,
      statusCounts: snapshot.status_counts,
    });
  };

  if (!selectedVersion) {
    return (
      <PageTransitionStyled>
        <PageHeaderStyled>
          <HeaderLeftStyled>
            <BackLinkStyled onClick={() => navigate("/talent-mobility")}>
              <ArrowLeftOutlined /> Back to Talent Mobility
            </BackLinkStyled>
            <HeadingRowStyled>
              <Title level={4}>PAIC Org Redesign</Title>
            </HeadingRowStyled>
            <Text type="secondary">
              Pick an already-approved Organisation Design version (each carries the PAIC group/version it was generated from) to
              begin redeployment/hiring execution against its approved headcount.
            </Text>
          </HeaderLeftStyled>
        </PageHeaderStyled>

        <ClickableTableWrapperStyled>
          <SbTable
            columns={[
              { title: "Version", dataIndex: "label" },
              { title: "Source", dataIndex: "sourceLabel" },
              { title: "Created", dataIndex: "createdAt", render: (v) => new Date(v).toLocaleDateString() },
              { title: "As-Is HC", dataIndex: "asIsHc", align: "center" },
              { title: "Total After HC", dataIndex: "totalAfterHc", align: "center" },
              { title: "Status", dataIndex: "status", render: (s) => <SbTag color="success">{s}</SbTag> },
            ]}
            dataSource={versions}
            rowKey="versionId"
            onRow={(record) => ({ onClick: () => setSelectedVersionId(record.versionId) })}
          />
        </ClickableTableWrapperStyled>

        {results.length > 0 && (
          <>
            <HeadingRowStyled style={{ padding: "16px 24px 0" }}>
              <Title level={5}>Previous Simulations</Title>
            </HeadingRowStyled>
            <ClickableTableWrapperStyled>
              <SbTable
                columns={[
                  { title: "Version", dataIndex: "versionLabel" },
                  { title: "Saved", dataIndex: "savedAt", render: (v) => new Date(v).toLocaleString() },
                  { title: "Reduced", key: "reduced", render: (_, r) => (r.statusCounts?.REDUCED || 0) + (r.statusCounts?.REDUNDANT || 0) },
                  { title: "Augmented", key: "augmented", render: (_, r) => r.statusCounts?.["AI AUGMENTED"] || 0 },
                  { title: "Added", key: "added", render: (_, r) => r.statusCounts?.CREATED || 0 },
                ]}
                dataSource={results}
                rowKey="resultId"
                pagination={false}
              />
            </ClickableTableWrapperStyled>
          </>
        )}
      </PageTransitionStyled>
    );
  }

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <BackLinkStyled onClick={() => setSelectedVersionId(null)}>
            <ArrowLeftOutlined /> Back to Versions
          </BackLinkStyled>
          <HeadingRowStyled>
            <Title level={4}>{selectedVersion.label} — {selectedVersion.sourceLabel}</Title>
          </HeadingRowStyled>
        </HeaderLeftStyled>
        <HeaderRightStyled>
          <SbButton buttonType="secondary" icon={<FileExcelOutlined />} onClick={handleExportCsv}>
            Export CSV
          </SbButton>
          <SbButton buttonType="primary" onClick={handleSaveResults}>
            Save Results
          </SbButton>
        </HeaderRightStyled>
      </PageHeaderStyled>

      {buckets && (
        <SummaryBucketsRowStyled>
          <SummaryBucketCardStyled><div className="count">{buckets.decreased}</div><div className="label">Roles Decreased</div></SummaryBucketCardStyled>
          <SummaryBucketCardStyled><div className="count">{buckets.augmented}</div><div className="label">Roles Augmented (No HC Change)</div></SummaryBucketCardStyled>
          <SummaryBucketCardStyled><div className="count">{buckets.increased}</div><div className="label">Roles Increased</div></SummaryBucketCardStyled>
          <SummaryBucketCardStyled><div className="count">{buckets.noChange}</div><div className="label">No Change</div></SummaryBucketCardStyled>
        </SummaryBucketsRowStyled>
      )}

      <SbTabs
        items={[
          { key: "reduced", label: "Roles Reduced", children: <ReducedRolesTab roles={snapshot.roles} design={snapshot.design} /> },
          { key: "augmented", label: "Roles Augmented", children: <AugmentedRolesTab roles={snapshot.roles} design={snapshot.design} /> },
          { key: "added", label: "Roles Added", children: <AddedRolesTab newRoles={snapshot.new_roles} /> },
        ]}
      />
    </PageTransitionStyled>
  );
};

export default PaicOrgRedesignV2;
